export interface Storage<TItem = string | null> {
  getItem: (key: string) => TItem;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
  onError: (error: unknown) => void;
}

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const EXPIRES_KEY = 'expires';
const FULL_NAME_KEY = 'fullName';
const EMAIL = 'email';
const PROFILE_IMAGE_KEY = 'profileImage';

class AuthStorage {
  private _accessToken: string | null = null;
  private _refreshToken: string | null = null;
  private _expires: number | null = null;
  private _fullName: string | null = null;
  private _email: string | null = null;
  private _profileImage: string | null = null;
  private _storage: Storage | null = null;

  constructor(_storage: Storage) {
    try {
      this._storage = _storage;
      this.accessToken = _storage.getItem(ACCESS_TOKEN_KEY);
      this.refreshToken = _storage.getItem(REFRESH_TOKEN_KEY);
      this.expires = Number(_storage.getItem(EXPIRES_KEY));
      this.fullName = _storage.getItem(FULL_NAME_KEY);
      this.email = _storage.getItem(EMAIL);
      this.profileImage = _storage.getItem(PROFILE_IMAGE_KEY);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      this._storage = null;
      this.accessToken = null;
      this.refreshToken = null;
      this.expires = null;
      this.fullName = null;
      this.email = null;
      this.profileImage = null;
    }
  }

  get accessToken(): string | null {
    return this._accessToken;
  }

  set accessToken(value: string | null) {
    this._accessToken = value;

    try {
      if (typeof value === 'string') {
        this._storage?.setItem(ACCESS_TOKEN_KEY, value);
      } else {
        this._storage?.removeItem(ACCESS_TOKEN_KEY);
      }
    } catch (error) {
      this._storage?.onError(error);
    }
  }

  get refreshToken(): string | null {
    return this._refreshToken;
  }

  set refreshToken(value: string | null) {
    this._refreshToken = value;

    try {
      if (typeof value === 'string') {
        this._storage?.setItem(REFRESH_TOKEN_KEY, value);
      } else {
        this._storage?.removeItem(REFRESH_TOKEN_KEY);
      }
    } catch (error) {
      this._storage?.onError(error);
    }
  }

  get expires(): number | null {
    return this._expires;
  }

  set expires(value: number | null) {
    this._expires = value;

    try {
      if (typeof value === 'number') {
        this._storage?.setItem(EXPIRES_KEY, value.toString());
      } else {
        this._storage?.removeItem(EXPIRES_KEY);
      }
    } catch (error) {
      this._storage?.onError(error);
    }
  }

  get fullName(): string | null {
    return this._fullName;
  }

  set fullName(value: string | null) {
    this._fullName = value;

    try {
      if (typeof value === 'string' && value.length > 0) {
        this._storage?.setItem(FULL_NAME_KEY, value);
      } else {
        this._storage?.removeItem(FULL_NAME_KEY);
      }
    } catch (error) {
      this._storage?.onError(error);
    }
  }

  get email(): string | null {
    return this._email;
  }

  set email(value: string | null) {
    this._email = value;

    try {
      if (typeof value === 'string' && value.length > 0) {
        this._storage?.setItem(EMAIL, value);
      } else {
        this._storage?.removeItem(EMAIL);
      }
    } catch (error) {
      this._storage?.onError(error);
    }
  }

  get profileImage(): string | null {
    return this._profileImage;
  }

  set profileImage(value: string | null) {
    this._profileImage = value;

    try {
      if (typeof value === 'string' && value.length > 0) {
        this._storage?.setItem(PROFILE_IMAGE_KEY, value);
      } else {
        this._storage?.removeItem(PROFILE_IMAGE_KEY);
      }
    } catch (error) {
      this._storage?.onError(error);
    }
  }
}

const storage: Storage = {
  getItem: (key: string) => sessionStorage.getItem(key),
  setItem: (key: string, value: string) => sessionStorage.setItem(key, value),
  removeItem: (key: string) => sessionStorage.removeItem(key),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onError: (_error: unknown) => {
    // handle errors here
  },
};

export const authStorage = new AuthStorage(storage);
